<template>
    <pendo-modal
        data-cy="product-area-modal"
        theme="app"
        :title="modalTitle"
        :visible="visible"
        :height="360"
        :width="600"
        @open="open"
        @close="close"
        @closed="closed">
        <template #body>
            <div
                v-if="edit"
                class="count-banner">
                <pendo-loading-indicator
                    :text="$t('groupModal.loadingIndicator.Checking for Product Area Members...')"
                    text-class="banner-text"
                    :loading="loading"
                    size="medium" />
                <span v-if="!loading">{{
                    isEmpty
                        ? $t(
                            'groupModal.groupIsEmpty.This Product Area is not assigned to any items, such as {productName} Features or Ideas in Validate.',
                            { productName: isAdoptEndUser ? trainingAttributes?.productName || 'OEM' : 'Pendo' }
                        )
                        : $t(
                            'groupModal.groupIsNotEmpty.This Product Area is assigned to one or more items, such as a {productName} Feature or Idea in Validate.',
                            { productName: isAdoptEndUser ? trainingAttributes?.productName || 'OEM' : 'Pendo' }
                        )
                }}</span>
            </div>
            <template v-else>
                <i18n-t
                    keypath="groupModal.groupCountBanner.message"
                    tag="div"
                    class="count-banner">
                    <template #prefix>
                        {{ groupCountPrefix }}
                    </template>
                    <template #groupCount>
                        <strong>{{ groupsCountLabel }}</strong>
                    </template>
                </i18n-t>
            </template>
            <pendo-form
                v-model="isValid"
                :model="groupModel"
                @submit="handleSubmit">
                <pendo-form-item
                    :rules="rules.name"
                    prop="name"
                    :label="$t('groupModal.form.Product Area Name')">
                    <pendo-input
                        v-model="groupModel.name"
                        data-cy="group-name-input"
                        :placeholder="$t('groupModal.form.Unnamed')"
                        :autofocus="true" />
                </pendo-form-item>
                <pendo-form-item>
                    <pendo-color-picker-presets
                        :value="groupModel.color"
                        :presets="colors"
                        :size="24"
                        :preset-labels-map="computedColorLabels"
                        @change="(color) => (groupModel.color = color)" />
                </pendo-form-item>
            </pendo-form>
        </template>
        <template #footer>
            <span>
                <pendo-button
                    data-cy="group-modal-cancel-button"
                    theme="app"
                    type="secondary"
                    :label="$t('groupModal.footer.Cancel')"
                    @click="close" />
                <pendo-button
                    theme="app"
                    type="primary"
                    :label="confirmButtonLabel"
                    :loading="saving"
                    :disabled="!isValid || !isDirty"
                    @click="confirmButton" />
            </span>
            <span v-if="edit">
                <pendo-button
                    class="delete-button"
                    theme="app"
                    type="danger"
                    :label="$t('groupModal.footer.Delete Product Area')"
                    :loading="deleting"
                    :disabled="loading"
                    @click="confirmDelete" />
            </span>
        </template>
    </pendo-modal>
</template>

<script>
import { GROUP_COLORS } from '@/constants';
import {
    PendoColorPickerPresets,
    PendoForm,
    PendoFormItem,
    PendoInput,
    PendoLoadingIndicator,
    PendoModal,
    PendoNotification,
    PendoButton,
    PendoTooltip,
} from '@pendo/components';
import { mapGetters, mapActions } from 'vuex';
import clone from 'lodash/clone';
import defaults from 'lodash/defaults';
import { fetchGroupIsEmpty } from '@/vuex/modules/groups/groups.utils';

const initialFormValues = ({ name, color } = {}) => {
    const defaultValues = {
        name: '',
        color: GROUP_COLORS.defaultColor,
    };

    return defaults(
        {
            name,
            color: GROUP_COLORS.colorMap[color],
        },
        defaultValues,
    );
};

export default {
    name: 'GroupModal',
    components: {
        PendoColorPickerPresets,
        PendoForm,
        PendoFormItem,
        PendoInput,
        PendoLoadingIndicator,
        PendoModal,
        PendoButton,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        edit: {
            type: Boolean,
            default: false,
        },
        group: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['close', 'closed', 'create-group', 'delete-group', 'update-group'],
    data () {
        return {
            localGroup: null,
            colors: GROUP_COLORS.colors.slice(0, -1),
            groupModel: initialFormValues(),
            isValid: false,
            rules: {
                name: [
                    {
                        required: true,
                        message: '',
                        trigger: ['blur', 'change'],
                        whitespace: true,
                    },
                    {
                        max: 64,
                        message: '',
                        trigger: ['blur', 'change'],
                    },
                    {
                        validator: this.nameIsDuplicate,
                        trigger: ['blur', 'change'],
                    },
                ],
            },
            saving: false,
            deleting: false,
            isEmpty: false,
            loading: true,
        };
    },
    computed: {
        ...mapGetters({
            list: 'groups/list',
            isAdoptEndUser: 'subscription/isAdoptEndUser',
            trainingAttributes: 'subscription/trainingAttributes',
        }),
        groupNames () {
            return this.list.map((group) => group.name?.toLowerCase());
        },
        groupCountPrefix () {
            return this.$t('groupModal.groupCountPrefix.There {list, plural, one {is} other {are}}', {
                list: this.list.length || 0,
            });
        },
        groupsCountLabel () {
            return this.$t('groupModal.groupCountBanner.label', { list: this.list.length || 0 });
        },
        modalTitle () {
            return this.edit
                ? this.$t('groupModal.title.Edit Product Area')
                : this.$t('groupModal.title.Create Product Area');
        },
        confirmButtonLabel () {
            return this.edit
                ? this.$t('groupModal.confirm.Save Product Area')
                : this.$t('groupModal.confirm.Create Product Area');
        },
        isDirty () {
            if (!this.group.id) return true;

            const { name, color } = this.groupModel;

            return name !== this.group.name || color !== GROUP_COLORS.colorMap[this.group.color];
        },
        computedColorLabels () {
            return GROUP_COLORS.colorLabelsMap;
        },
    },
    created () {
        this.rules.name[0].message = this.$t('groupModal.form.rule.Product Area name is required');
        this.rules.name[1].message = this.$t('groupModal.form.rule.Product Area name must be 64 characters or less');
    },
    methods: {
        ...mapActions('groups', ['create', 'update', 'remove']),
        open () {
            this.isEmpty = false;
            this.groupModel = initialFormValues(this.group);
            this.localGroup = this.group?.id ? clone(this.group) : null;
            if (this.edit) {
                this.fetchGroupIsEmpty(this.group.id);
            }
        },
        close () {
            this.$emit('close');
        },
        closed () {
            this.isEmpty = false;
            this.isValid = false;
            this.localGroup = null;
            this.$emit('closed');
        },
        async confirmButton () {
            this.saving = true;
            const groupData = {
                name: this.groupModel.name,
                color: GROUP_COLORS.colorClassMap[this.groupModel.color],
            };

            let notificationMessages;

            try {
                if (this.edit) {
                    this.localGroup = {
                        ...this.localGroup,
                        ...groupData,
                    };
                    await this.update(this.localGroup);
                    this.$emit('update-group', this.localGroup);
                } else {
                    this.localGroup = await this.create(groupData);
                    this.$emit('create-group', this.localGroup);
                }

                notificationMessages = this.getNotificationMessages();
                const notificationMessagesToUse = notificationMessages[this.edit ? 'update' : 'create'].success;

                PendoNotification({
                    type: 'success',
                    title: notificationMessagesToUse.title,
                    message: notificationMessagesToUse.message,
                    duration: 3000,
                });
                this.close();
            } catch {
                notificationMessages = this.getNotificationMessages();
                const notificationMessagesToUse = notificationMessages[this.edit ? 'update' : 'create'].failure;

                PendoNotification({
                    type: 'error',
                    title: notificationMessagesToUse.title,
                    message: notificationMessagesToUse.message,
                    duration: 3000,
                });
            } finally {
                this.saving = false;
            }
        },
        handleSubmit () {
            if (this.isValid) {
                this.confirmButton();
            }
        },
        async confirmDelete () {
            this.deleting = true;

            let notificationMessages;

            try {
                await this.remove({ id: this.localGroup.id });

                notificationMessages = this.getNotificationMessages();
                const notificationMessagesToUse = notificationMessages.delete.success;

                PendoNotification({
                    type: 'success',
                    title: notificationMessagesToUse.title,
                    message: notificationMessagesToUse.message,
                    duration: 3000,
                });
                this.$emit('delete-group');
                this.close();
            } catch {
                notificationMessages = this.getNotificationMessages();
                const notificationMessagesToUse = notificationMessages.delete.failure;

                PendoNotification({
                    type: 'error',
                    title: notificationMessagesToUse.title,
                    message: notificationMessagesToUse.message,
                    duration: 3000,
                });
            } finally {
                this.deleting = false;
            }
        },
        nameIsDuplicate (rule, val, cb) {
            if (this.edit && val === this.localGroup.name) {
                cb();
            } else if (
                this.edit &&
                val !== this.localGroup.name &&
                this.groupNames.includes(val?.toLowerCase().trim())
            ) {
                cb(this.$t('groupModal.nameIsDuplicate.edit.Product Area name already in use'));
            } else if (!this.edit && this.groupNames.includes(val?.toLowerCase().trim())) {
                cb(this.$t('groupModal.nameIsDuplicate.create.Product Area name already in use'));
            } else {
                cb();
            }
        },
        async fetchGroupIsEmpty (groupId) {
            this.loading = true;
            const res = await fetchGroupIsEmpty({ id: groupId });
            this.loading = false;
            this.isEmpty = res.data.isempty;
        },
        getNotificationMessages () {
            return {
                create: {
                    success: {
                        title: this.$t('groupModal.confirm.notification.title.Product Area created.'),
                        message: this.$t(
                            'groupModal.confirm.notification.message.Product Area {name} was created successfully.',
                            { name: this.localGroup.name },
                        ),
                    },
                    failure: {
                        title: this.$t('groupModal.confirm.notification.error.title.Something went wrong.'),
                        message: this.$t(
                            'groupModal.confirm.notification.error.message.Failed to create Product Area. Please try again.',
                        ),
                    },
                },
                update: {
                    success: {
                        title: this.$t('groupModal.confirm.notification.title.Product Area updated.'),
                        message: this.$t(
                            'groupModal.confirm.notification.message.Product Area {name} was updated successfully.',
                            { name: this.localGroup.name },
                        ),
                    },
                    failure: {
                        title: this.$t('groupModal.confirm.notification.error.title.Something went wrong.'),
                        message: this.$t(
                            'groupModal.confirm.notification.error.message.Failed to update Product Area. Please try again.',
                        ),
                    },
                },
                delete: {
                    success: {
                        title: this.$t('groupModal.delete.notification.title.Product Area deleted.'),
                        message: this.$t(
                            'groupModal.delete.notification.message.Product Area was deleted successfully.',
                        ),
                    },
                    failure: {
                        title: this.$t('groupModal.delete.notification.error.title.Something went wrong.'),
                        message: this.$t(
                            'groupModal.delete.notification.error.message.Failed to delete Product Area. Please try again.',
                        ),
                    },
                },
            };
        },
    },
};
</script>

<style lang="less" scoped>
.count-banner {
    padding: 16px;
    margin: -24px -20px 20px;
    background: var(--palette-gray-10);
    border-bottom: 1px solid var(--palette-gray-40);
    text-align: center;

    .banner-text {
        font-size: 12px;
    }
}

:deep(.pendo-form-item) {
    margin-bottom: 24px;
}

:deep(.pendo-modal__footer) {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
}
</style>
